import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  TCallState,
  TIncomingCall,
  TCDRData,
  TNewUserForm,
} from "../../types/callCenter.types";

const MAX_DISMISSED_IDS = 64;

function pushDismissed(list: string[], callId: string): void {
  if (list.includes(callId)) return;
  list.push(callId);
  while (list.length > MAX_DISMISSED_IDS) {
    list.shift();
  }
}

interface ICallCenterState {
  callState: TCallState;
  currentCall: TIncomingCall | null;
  cdrData: TCDRData | null;
  isRecording: boolean;
  recordingPaused: boolean;
  callDuration: number;
  newUserForm: TNewUserForm | null;
  /** Rejected callIds — do not show MVoiceIncomingCall again for these in this session. */
  dismissedCallIds: string[];
}

const initialState: ICallCenterState = {
  callState: "idle",
  currentCall: null,
  cdrData: null,
  isRecording: false,
  recordingPaused: false,
  callDuration: 0,
  newUserForm: null,
  dismissedCallIds: [],
};

const callCenterSlice = createSlice({
  name: "callCenter",
  initialState,
  reducers: {
    setIncomingCall(state, action: PayloadAction<TIncomingCall>) {
      if (state.dismissedCallIds.includes(action.payload.callId)) {
        return;
      }
      // PaycallCallStart for therapist-initiated dials — keep ActiveCallWidget, not incoming popup.
      if (action.payload.isOutbound) {
        return;
      }
      if (state.currentCall?.isOutbound && state.callState === "active") {
        return;
      }
      state.callState = "ringing";
      state.currentCall = action.payload;
      state.callDuration = 0;
    },

    setOutboundRemoteAnswered(state, action: PayloadAction<boolean>) {
      if (!state.currentCall?.isOutbound) {
        return;
      }
      state.currentCall.remoteAnswered = action.payload;
      if (action.payload) {
        state.callDuration = 0;
      }
    },

    /**
     * Apply GET /MVoicePBX/active-calls before SignalR connects (page refresh).
     * Merge by callId; does not override active/ended UI. SignalR remains source of truth for ended.
     */
    hydrateMvoiceActiveCallsSnapshot(
      state,
      action: PayloadAction<TIncomingCall[]>
    ) {
      const incoming = action.payload
        .filter((c) => !state.dismissedCallIds.includes(c.callId))
        .slice()
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

      if (incoming.length === 0) return;

      if (state.callState === "active" || state.callState === "ended") {
        return;
      }

      if (state.callState === "ringing" && state.currentCall) {
        const match = incoming.find(
          (c) => c.callId === state.currentCall!.callId
        );
        if (match) {
          state.currentCall = match;
        }
        return;
      }

      state.callState = "ringing";
      state.currentCall = incoming[0]!;
      state.callDuration = 0;
    },

    setCallActive(state) {
      if (!state.currentCall) {
        return;
      }
      state.callState = "active";
      state.isRecording = true;
      state.recordingPaused = false;
    },

    /** Therapist-initiated outbound dial — show active-call widget immediately. */
    startOutgoingCall(
      state,
      action: PayloadAction<{
        callId: string;
        phone: string;
        userId?: number;
        guid?: string;
        firstName?: string | null;
        lastName?: string | null;
      }>
    ) {
      const { callId, phone, userId, guid, firstName, lastName } =
        action.payload;
      const trimmedPhone = phone.trim();
      const hasName = Boolean(
        firstName?.trim() || lastName?.trim() || userId
      );

      state.callState = "active";
      state.isRecording = true;
      state.recordingPaused = false;
      state.callDuration = 0;
      state.cdrData = null;
      state.currentCall = {
        callId,
        cli: trimmedPhone,
        isIdentified: hasName,
        timestamp: new Date().toISOString(),
        isOutbound: true,
        remoteAnswered: false,
        ...(hasName
          ? {
              callerInfo: {
                userId: userId ?? 0,
                guid: guid?.trim() ?? "",
                firstName: firstName?.trim() ?? "",
                lastName: lastName?.trim() ?? "",
                phone: trimmedPhone,
                currentCycle: 0,
                previousCycles: 0,
              },
            }
          : {}),
      };
    },

    setCallEnded(state, action: PayloadAction<TCDRData>) {
      state.callState = "ended";
      state.cdrData = action.payload;
      state.isRecording = false;
      state.recordingPaused = false;
    },

    /** Apply CDR from MVoice when it matches the active or idle-waiting context call. */
    applyMvoiceCallEnded(state, action: PayloadAction<TCDRData>) {
      const cdr = action.payload;
      
      
      if (state.dismissedCallIds.includes(cdr.callId)) {
        return;
      }
      if (!state.currentCall) {
        return;
      }
      const callIdMatches = state.currentCall.callId === cdr.callId;
      const outboundRemoteEnd =
        state.currentCall.isOutbound &&
        state.callState === "active" &&
        cdr.direction?.toLowerCase() === "outbound";
      if (!callIdMatches && !outboundRemoteEnd) {
        return;
      }
      const resolvedCdr = callIdMatches
        ? cdr
        : { ...cdr, callId: state.currentCall.callId };
      // Still ringing: another agent answered, PBX cleared, or remote disconnect before we acted — close UI only, no document.
      if (state.callState === "ringing") {
        state.currentCall = null;
        state.callState = "idle";
        state.callDuration = 0;
        return;
      }
      // If already idle (manual hangup via endActiveCallWidget), don't override
      if (state.callState === "idle") {
        return;
      }
      if (
        state.callState === "ended" &&
        state.currentCall.callId === resolvedCdr.callId
      ) {
        return;
      }
      // Set ended state for both inbound and outbound calls
      state.callState = "ended";
      state.cdrData = resolvedCdr;
      state.isRecording = false;
      state.recordingPaused = false;
    },

    /** Attach meeting recordNumber when the separate SignalR event arrives. */
    attachCallRecordNumber(
      state,
      action: PayloadAction<{ callId: string; recordNumber: number }>
    ) {
      const { callId, recordNumber } = action.payload;
      if (!state.cdrData || state.cdrData.callId !== callId) {
        return;
      }
      state.cdrData.recordNumber = recordNumber;
    },

    /** Clears outbound / local active widget (inbound waits for PaycallCallEnd CDR). */
    endActiveCallWidget(state) {
      // For outbound calls, clear regardless of callState (may be ringing, active, or already idle)
      // For inbound calls, only clear if active (ended state is handled by applyMvoiceCallEnded)
      const isOutbound = state.currentCall?.isOutbound === true;
      if (!isOutbound && state.callState !== "active") {
        return;
      }
      state.callState = "idle";
      state.currentCall = null;
      state.isRecording = false;
      state.recordingPaused = false;
      state.callDuration = 0;
      console.log('[DEBUG endActiveCallWidget] STATE CLEARED');
    },

    /** PaycallCallTaken — another agent answered, or this agent did (answeredByMe). */
    applyPaycallCallTaken(
      state,
      action: PayloadAction<{ callId: string; answeredByMe: boolean }>
    ) {
      const { callId, answeredByMe } = action.payload;
      if (state.dismissedCallIds.includes(callId)) {
        return;
      }
      if (!state.currentCall || state.currentCall.callId !== callId) {
        return;
      }
      if (state.callState !== "ringing") {
        return;
      }
      if (answeredByMe) {
        state.callState = "active";
        state.isRecording = true;
        state.recordingPaused = false;
      } else {
        state.currentCall = null;
        state.callState = "idle";
        state.callDuration = 0;
      }
    },

    rejectIncomingCall(state, action: PayloadAction<string>) {
      const callId = action.payload;
      pushDismissed(state.dismissedCallIds, callId);
      if (state.currentCall?.callId === callId) {
        state.currentCall = null;
        state.callState = "idle";
        state.callDuration = 0;
      }
    },

    clearCall(state) {
      state.callState = "idle";
      state.currentCall = null;
      state.cdrData = null;
      state.isRecording = false;
      state.recordingPaused = false;
      state.callDuration = 0;
      state.newUserForm = null;
      state.dismissedCallIds = [];
    },

    toggleRecordingPause(state) {
      state.recordingPaused = !state.recordingPaused;
    },

    incrementCallDuration(state) {
      if (state.callState !== "active") {
        return;
      }
      if (state.currentCall?.isOutbound && !state.currentCall.remoteAnswered) {
        return;
      }
      state.callDuration += 1;
    },

    setNewUserForm(state, action: PayloadAction<TNewUserForm>) {
      state.newUserForm = action.payload;
    },
  },
});

export const {
  setIncomingCall,
  setOutboundRemoteAnswered,
  hydrateMvoiceActiveCallsSnapshot,
  setCallActive,
  startOutgoingCall,
  setCallEnded,
  applyMvoiceCallEnded,
  applyPaycallCallTaken,
  attachCallRecordNumber,
  endActiveCallWidget,
  rejectIncomingCall,
  clearCall,
  toggleRecordingPause,
  incrementCallDuration,
  setNewUserForm,
} = callCenterSlice.actions;

export default callCenterSlice.reducer;
